import { useTranslation } from 'react-i18next'
import { Modal } from '@symphenia/ui-components'
import { api } from '@/lib/axios'
import type { User } from '@/lib/users/user.types'
import {
  FeldStatus,
  mitStand,
  useAenderung,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'

/** Die festen Projektrollen — die Phasen kommen vom Server dazu. */
export const FESTE_PROJEKTROLLEN = ['LEAD', 'SALES', 'CALCULATION', 'FPC', 'WELDING_COORDINATOR'] as const

/** Eine aktive Projektphase, so weit dieser Dialog sie braucht. */
export interface ProjektPhase {
  key: string
  name: string
}

/**
 * AP-35 — welche Rollen ein Mitarbeiter im Projekt übernehmen kann.
 *
 * Die Auswahlfelder im Projekt (Projektleitung, Verkauf, Kalkulation, je Phase
 * der Zuständige) bieten nur an, wer die passende Rolle trägt; trägt sie
 * niemand, bieten sie alle an. So bleibt ein Mandant, der das nie pflegt,
 * arbeitsfähig.
 *
 * AP-36 Phase E (S-17): Jedes Kästchen speichert **sofort**, als Operation
 * „Schlüssel hinzu bzw. weg" auf der gespeicherten Liste (8.3 a). Schlüssel, die
 * hier nicht angeboten werden (eine inzwischen abgeschaltete Phase), bleiben
 * deshalb erhalten — auch wenn zwei Kästchen kurz hintereinander fallen. Der
 * Knopf heißt „Schließen": Zu speichern gibt es nichts mehr.
 */
export function ProjectRolesDialog({
  user,
  phasen,
  phasenFehler,
  onClose,
  onSaved,
}: {
  user: User
  phasen: ProjektPhase[]
  /** Die Phasen ließen sich nicht laden — die festen Rollen gehen trotzdem. */
  phasenFehler: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `user:${user.id}`,
    bezeichnung: [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ') || user.email || '—',
    daten: user as unknown as Datensatz,
    // Die Rollen stehen im Profil; die Stelle führt sie flach, wie das PATCH sie nimmt.
    werte: (antwort) => {
      const u = (antwort ?? {}) as Datensatz
      const profil = (u.profile ?? {}) as Datensatz
      // Der Stand ist der der **Benutzerzeile**: Der Server fasst sie auch dann an,
      // wenn nur das Profil geschrieben wird.
      return { projectRoles: profil.projectRoles ?? [], updatedAt: u.updatedAt }
    },
    schreibe: (patch, stand) =>
      api.patch<Datensatz>(`/users/${user.id}`, patch, mitStand(stand)).then((r) => {
        onSaved()
        return r.data
      }),
  }) as Speicherstelle<unknown>
  const gewaehlt = new Set(((useStellenDaten(stelle).projectRoles as string[] | undefined) ?? []))
  const rollen = useAenderung(stelle, `user:${user.id}:projectRoles`)

  const umschalten = (key: string) =>
    void rollen.sende({
      felder: ['projectRoles'],
      labels: { projectRoles: t('users.projectRoles.title') },
      // Eigene Kennung je Schlüssel: „A hinzu" und „B weg" dürfen einander nicht ersetzen.
      wert: key,
      operation: (aktuell) => {
        const liste = new Set(((aktuell.projectRoles as string[] | undefined) ?? []))
        if (liste.has(key)) liste.delete(key)
        else liste.add(key)
        return { projectRoles: [...liste] }
      },
    })

  const name = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ')

  const feld = (key: string, label: string) => (
    <label key={key} className="flex items-center gap-2 text-sm text-foreground">
      <input
        type="checkbox"
        checked={gewaehlt.has(key)}
        onChange={() => umschalten(key)}
        className="h-4 w-4 rounded border-input"
      />
      {label}
    </label>
  )

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={t('users.projectRoles.title')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t('common.close', { defaultValue: 'Schließen' })}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-foreground">
          {name || user.email} <span className="text-muted-foreground">({user.email})</span>
        </p>

        <div className="space-y-2">
          {FESTE_PROJEKTROLLEN.map((key) => feld(key, t(`users.projectRoles.${key}`)))}
        </div>

        {phasen.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t('users.projectRoles.phases')}
            </p>
            {phasen.map((p) => feld(p.key, p.name))}
          </div>
        )}
        {phasenFehler && (
          <p className="text-xs text-muted-foreground">{t('users.projectRoles.phasesFailed')}</p>
        )}

        <p className="text-xs text-muted-foreground">{t('users.projectRoles.hint')}</p>
        <FeldStatus status={rollen.status} />
      </div>
    </Modal>
  )
}
